import { ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { ReportEmailAccessBanner, ReportEmailDeliveryForm, WhatYoullReceivePanel } from "@visioni/a3s-reports/app";
import { View } from "@src/components";
import { isWeb } from "@src/utils/web-helpers";
import {
  useReportEmailDeliveryPage,
  type ReportEmailDeliveryPageReady,
} from "@src/features/roadsense/email-delivery/useReportEmailDeliveryPage";
import {
  ReportEmailDeliveryForbidden,
  ReportEmailDeliveryLoadFailed,
  ReportEmailDeliveryLoading,
  ReportEmailDeliveryNotFound,
  ReportEmailDeliveryWebOnly,
} from "@src/features/roadsense/email-delivery/ReportEmailDeliveryStates";

/**
 * One site's RoadSense **report emails** at
 * `…/reports/roadsense/report/<reportId>/site/<siteId>/email-delivery`: set
 * them up, change them or stop them. The delivery is per site; the report is
 * only in the path so the breadcrumbs lead back to it. Every report email
 * links here (its `manage_url`).
 */
export default function RoadSenseReportEmailDeliveryScreen() {
  const params = useLocalSearchParams<{ siteId?: string }>();

  if (!isWeb) return <ReportEmailDeliveryWebOnly />;

  return (
    <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
      <Body siteIdParam={params.siteId} />
    </ScrollView>
  );
}

function Body({ siteIdParam }: { siteIdParam: string | string[] | undefined }) {
  const page = useReportEmailDeliveryPage(siteIdParam);

  switch (page.kind) {
    case "loading":
      return <ReportEmailDeliveryLoading />;
    case "notFound":
      return <ReportEmailDeliveryNotFound />;
    case "forbidden":
      return <ReportEmailDeliveryForbidden />;
    case "failed":
      return <ReportEmailDeliveryLoadFailed onRetry={page.retry} />;
    case "ready":
      return <Ready page={page} />;
  }
}

function Ready({ page }: { page: ReportEmailDeliveryPageReady }) {
  const accessBanner =
    page.accessBannerReason !== null ? <ReportEmailAccessBanner reason={page.accessBannerReason} /> : undefined;
  const common = {
    siteName: page.siteName,
    email: page.email,
    form: page.form,
    isSubmitting: page.isSubmitting,
    accessBanner,
    now: page.now,
    viewerTimeZone: page.viewerTimeZone,
  };

  return (
    <View style={styles.columns}>
      <View style={styles.formColumn}>
        {page.saved !== undefined ?
          <ReportEmailDeliveryForm
            {...common}
            mode="active"
            saved={page.saved}
            onSave={page.save}
            onStop={page.stop}
            isStopping={page.isStopping}
          />
        : <ReportEmailDeliveryForm {...common} mode="new" onStart={page.start} />}
      </View>
      <View style={styles.panelColumn}>
        <WhatYoullReceivePanel
          siteName={page.siteName}
          exampleFileName={page.exampleFileName}
          now={page.now}
          viewerTimeZone={page.viewerTimeZone}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  // Matches the RoadSense site report's padding.
  scrollContent: {
    padding: 20,
    width: "100%",
  },
  // Side by side when there's room for both minimum widths, stacked otherwise.
  columns: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: theme.spacing["6xl"],
  },
  formColumn: {
    flexGrow: 2,
    flexBasis: 360,
    maxWidth: 720,
  },
  panelColumn: {
    flexGrow: 1,
    flexBasis: 260,
    maxWidth: 400,
  },
}));
